"use client";

import { useState } from "react";
import { Header } from "@/components/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { EventStatusLegendCard } from "../../components/event-status-legend";
//import EventManagementPage from "../events/page"
//import supabase from "@/config/supabaseclient"
import { useContext } from "react";
import { EventsContext } from "../context/eventsContext";
import { appEvent } from "../sampleData/sampleEventsData";
import { EventCard } from "../events/event-card";

//import { CalendarGridCard } from "./calendar-grid"

 // Add these imports for opening event details dialog
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Users, DollarSign, Phone, Mail } from "lucide-react";


const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const eventStatusColors = {
  cancelled: "bg-red-600",
  completed: "bg-blue-600",
  inprogress: "bg-blue-600",
  approved: "bg-green-600",
  requested: "bg-yellow-600",
};

const eventStatusLabels = {
  cancelled: "Cancelled",
  completed: "Completed",
  inprogress: "In Progress",
  approved: "Approved",
  requested: "Requested",
  planned: "Planned",
};

export default function CalendarPage() {

  // Add these new state variables for opening event details dialog
  const [selectedEvent, setSelectedEvent] = useState<appEvent | null>(null);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  
  // Access events from context
  const { events } = useContext(EventsContext);
  const [currentDate, setCurrentDate] = useState(new Date()); // Today
  const [view, setView] = useState<"month" | "week" | "today">("month");

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setView("today");
  };

  const formatDateKey = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  const getEventsForDate = (day: number): appEvent[] => {
    const date = new Date(year, month, day);
    const dateKey = formatDateKey(date);

    return events.filter((event) => {
      const eventDate =
        event.startDate instanceof Date
          ? event.startDate
          : new Date(event.startDate ?? "");
      return formatDateKey(eventDate) === dateKey;
    });
  };

  const renderCalendarDays = () => {
    const days = [];
    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === year && today.getMonth() === month;
    const todayDate = today.getDate();

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="h-24 border border-border bg-muted/20"
        />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = isCurrentMonth && day === todayDate;
      const events: appEvent[] = getEventsForDate(day);
      //console.log("Calendar: Events for day", day, events);

      days.push(
        <div
          key={day}
          className={cn(
            "h-24 border border-border bg-background p-2 cursor-pointer hover:bg-muted/50 transition-colors",
            isToday && "bg-primary/10 border-primary"
          )}
        >
          <div
            className={cn(
              "text-sm font-medium mb-1",
              isToday && "text-primary"
            )}
          >
            {day}
          </div>
          <div className="space-y-1">
            {events.map((event) => (
              <div
                key={event.id}
                className={cn(
                  "text-xs px-2 py-1 rounded text-white truncate cursor-pointer hover:opacity-80 transition-opacity",
                  eventStatusColors[
                    event.status as keyof typeof eventStatusColors
                  ]
                )}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent calendar day click
                  handleEventClick(event);
              }}
              >
                {event.name}
              </div>
            ))}
            {events.length > 2 && (
              <div className="text-xs text-muted-foreground px-2">
                +{events.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const handleEventClick = (event: appEvent) => {
    setSelectedEvent(event);
    setIsEventDialogOpen(true);
  };

  return (
    <>
      <Header
        title="Calendar"
        subtitle="Track event requests and scheduled events"
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">

          {/* Calendar Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={previousMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold min-w-[200px] text-center">
                  {months[month]} {year}
                </h2>
                <Button variant="ghost" size="icon" onClick={nextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              {/* Today Button */}
              <Button
                variant={view === "today" ? "default" : "outline"}
                size="sm"
                onClick={goToToday}
              >
                Today
              </Button>

              {/* This Week Button */}
              <Button
                variant={view === "week" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("week")}
              >
                Week
              </Button>

              {/* This Month Button */}
              <Button
                variant={view === "month" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("month")}
              >
                Month
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <Card>
            <CardContent className="p-0">
              {/* Days of Week Header */}
              <div className="grid grid-cols-7 border-b">
                {daysOfWeek.map((day) => (
                  <div
                    key={day}
                    className="p-4 text-center text-sm font-medium text-muted-foreground bg-muted/30 border-r border-border last:border-r-0"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7">{renderCalendarDays()}</div>
            </CardContent>
          </Card>

          {/* Event Status Legend */}
          <EventStatusLegendCard />

          {/* Events this month */}
          <Card>
            <CardHeader>
              <CardTitle>Events This Month</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-3">
                {events
                  .filter((event) => {
                    const eventDate =
                      event.startDate
                        ? new Date(event.startDate)
                        : new Date();
                    return (
                      eventDate.getFullYear() === year &&
                      eventDate.getMonth() === month
                    );
                  })
                  .sort(
                    (a, b) =>
                      new Date(a.startDate ?? "").getTime() -
                      new Date(b.startDate ?? "").getTime()
                  )
                  .map((event: appEvent) => {
                    //const eventDate = new Date(event.startDate ?? "");
                    return (
                      <div key={event.id}>
                        {/* Event Card  */}
                        <EventCard
                          key={event.id}
                          {...event}
                          startDate = {event.startDate ? event.startDate.toString() : ""}
                          endDate = {event.endDate ? event.endDate.toString() : ""}
                          nonResStartDate ={event.nonResStartDate ? event.nonResStartDate.toString() : ""}
                          nonResEndDate = {event.nonResEndDate ? event.nonResEndDate.toString() : ""}
                        />
                         <span
                          className={cn(
                            "text-xs px-2 py-1 rounded text-white",
                            eventStatusColors[
                              event.status as keyof typeof eventStatusColors
                            ]
                          )}
                        >
                          {
                            eventStatusLabels[
                              event.status as keyof typeof eventStatusLabels
                            ]
                          }
                        </span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          {/* Event Management Section */}
          {/* TBD: Replace with a component */}
          {/*<EventManagementPage />*/}
        </div>
      </div>

      {/* Event Details Dialog */}
      <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEvent?.name}</DialogTitle>
            <DialogDescription>
              Event details and information
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-6 py-4">
              {/* Event Status */}
              <div className="flex items-center gap-2">
                <Badge 
                  className={cn(
                    "text-white",
                    eventStatusColors[selectedEvent.status as keyof typeof eventStatusColors]
                  )}
                >
                  {eventStatusLabels[selectedEvent.status as keyof typeof eventStatusLabels]}
                </Badge>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Start Date:</span>
                    <span>{selectedEvent.startDate ? new Date(selectedEvent.startDate).toLocaleDateString() : "N/A"}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">End Date:</span>
                    <span>{selectedEvent.endDate ? new Date(selectedEvent.endDate).toLocaleDateString() : "Same day"}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Location:</span>
                    <span>{selectedEvent.location || "TBD"}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Participants:</span>
                    <span>{selectedEvent.participants}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Budget:</span>
                    <span>₹{selectedEvent.budget?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div className="space-y-2">
                  <h4 className="font-medium">Description:</h4>
                  <p className="text-sm text-muted-foreground">{selectedEvent.description}</p>
                </div>
              )}

              {/* Requester Information */}
              <div className="space-y-2">
                <h4 className="font-medium">Requester Information:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Name:</span>
                    <span>{selectedEvent.requesterName}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Department:</span>
                    <span>{selectedEvent.department}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedEvent.mobileNum}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedEvent.emailId}</span>
                  </div>
                </div>
              </div>

              {/* Event Type and Nature */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-sm font-medium">Event Type:</span>
                  <p className="text-sm text-muted-foreground">{selectedEvent.type}</p>
                </div>
                
                <div className="space-y-1">
                  <span className="text-sm font-medium">Nature:</span>
                  <p className="text-sm text-muted-foreground">{selectedEvent.nature}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
